// components/FBAnalyticsDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  styled,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  TrendingUp,
  Restaurant,
  Receipt,
  AttachMoney,
  Schedule,
  CheckCircle,
  Cancel,
  Refresh,
  BarChart,
  PieChart,
  ShowChart,
  Search,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../../components/sidebars/sidebar_f&b';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Styled Components
const StatCard = styled(Card)(({ theme, color = 'primary' }) => ({
  background: `linear-gradient(135deg, ${theme.palette[color].light}20 0%, ${theme.palette[color].main}10 100%)`,
  border: `1px solid ${theme.palette[color].light}30`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  height: '400px',
  background: theme.palette.background.paper,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const FBAnalyticsDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || '';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return '';
    }
  };

  const getCurrentStaffId = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.staff_id || storedProfile.id || null;
  };

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const hotelCode = getHotelCode();
      const staffId = getCurrentStaffId();
      
      if (!hotelCode) {
        throw new Error('Hotel code not found');
      }

      const params = new URLSearchParams({
        hotel_code: hotelCode,
        period: period,
        ...(staffId && { staff_id: staffId })
      });

      const response = await fetch(`${API_BASE_URL}/hotel/F&B/statistics.php?${params}`);
      const data = await response.json();

      if (data.success) {
        setAnalytics(data.statistics);
        console.log('Analytics data loaded:', data.statistics);
      } else {
        throw new Error(data.error || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalytics();
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Chart data preparation from real database data
  const prepareHourlyData = () => {
    if (!analytics?.hourly_trends) return [];
    
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      hourLabel: `${i}:00`,
      orders: 0,
      revenue: 0
    }));

    analytics.hourly_trends.forEach(item => {
      const hour = parseInt(item.hour);
      if (hour >= 0 && hour < 24) {
        hours[hour].orders = parseInt(item.order_count) || 0;
        hours[hour].revenue = parseFloat(item.revenue) || 0;
      }
    });

    return hours;
  };

  const prepareDailyData = () => {
    if (!analytics?.daily_trends) return [];
    
    return analytics.daily_trends.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      orders: parseInt(item.order_count) || 0,
      revenue: parseFloat(item.revenue) || 0
    }));
  };

  const prepareOrderTypeData = () => {
    if (!analytics?.order_type_distribution) return [];
    
    const orderTypeLabels = {
      'dine_in': 'Dine In',
      'room_service': 'Room Service',
      'takeaway': 'Takeaway',
      'delivery': 'Delivery'
    };

    return analytics.order_type_distribution.map(item => ({
      name: orderTypeLabels[item.order_type] || item.order_type,
      value: parseInt(item.count) || 0,
      revenue: parseFloat(item.revenue) || 0
    }));
  };

  const prepareCategoryData = () => {
    if (!analytics?.category_performance) return [];
    
    return analytics.category_performance.map(item => ({
      name: item.category_name,
      revenue: parseFloat(item.total_revenue) || 0,
      quantity: parseInt(item.total_quantity) || 0
    }));
  };

  const preparePopularItemsData = () => {
    if (!analytics?.popular_items) return [];
    
    return analytics.popular_items.map(item => ({
      name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
      sales: parseInt(item.total_quantity) || 0,
      revenue: parseFloat(item.total_revenue) || 0
    }));
  };

  // Chart colors
  const CHART_COLORS = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const PIE_COLORS = [
    CHART_COLORS.primary,
    CHART_COLORS.secondary,
    CHART_COLORS.success,
    CHART_COLORS.warning,
    CHART_COLORS.error,
    CHART_COLORS.info,
  ];

  const tabLabels = [
    { label: 'Dashboard', icon: <TrendingUp /> },
   
    { label: 'Performance', icon: <BarChart /> },

  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderDashboardTab();
     
      case 1:
        return renderPerformanceTab();
      default:
        return renderDashboardTab();
    }
  };

  const renderDashboardTab = () => {
    const hourlyData = prepareHourlyData();
    const orderTypeData = prepareOrderTypeData();
    const popularItemsData = preparePopularItemsData();

    return (
        
      <Box>
      {/* Main Charts */}
        <Grid container spacing={3}>
          
          {/* Order Type Distribution */}
          <Grid item xs={12} lg={4}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary">
                <PieChart sx={{ verticalAlign: 'middle', mr: 1 }} />
                Order Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <RechartsPieChart>
                  <Pie
                    data={orderTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Orders']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Grid>

          {/* Popular Items */}
          <Grid item xs={12} lg={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary">
                <Restaurant sx={{ verticalAlign: 'middle', mr: 1 }} />
                Most Popular Items
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <RechartsBarChart data={popularItemsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="name" 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? `₦${Number(value).toFixed(2)}` : value,
                      name === 'revenue' ? 'Revenue' : 'Sales'
                    ]}
                  />
                  <Legend />
                  <Bar 
                    dataKey="sales" 
                    name="Quantity Sold"
                    fill={CHART_COLORS.primary}
                    radius={[4, 4, 0, 0]}
                  />
                </RechartsBarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} lg={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary">
                <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                Performance Summary
              </Typography>
              <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {analytics?.total_orders || 0}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Total Orders Today
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="success.main" fontWeight="bold">
                      {analytics?.completed_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Completed
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="warning.main" fontWeight="bold">
                      {analytics?.pending_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Pending
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="error.main" fontWeight="bold">
                      {analytics?.cancelled_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Cancelled
                    </Typography>
                  </Box>
                </Box>

                {analytics?.current_staff_sales > 0 && (
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'info.light', borderRadius: 2 }}>
                    <Typography variant="h6" color="info.dark">
                      Your Sales: ₦{analytics.current_staff_sales.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </ChartContainer>
          </Grid>
        </Grid>


<Grid sx={{ mb: 4 }}>
  <Box></Box>
</Grid>


        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Total Orders',
              value: analytics?.total_orders?.toLocaleString() || '0',
              icon: <Receipt />,
              color: 'primary',
            },
            {
              title: 'Total Revenue',
              value: `₦${(analytics?.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              icon: <AttachMoney />,
              color: 'success',
            },
            {
              title: 'Avg Order Value',
              value: `₦${(analytics?.average_order_value || 0).toFixed(2)}`,
              icon: <TrendingUp />,
              color: 'info',
            },
            {
              title: 'Completed Orders',
              value: analytics?.completed_orders?.toLocaleString() || '0',
              icon: <CheckCircle />,
              color: 'success',
            },
            {
              title: 'Pending Orders',
              value: analytics?.pending_orders?.toLocaleString() || '0',
              icon: <Schedule />,
              color: 'warning',
            },
            {
              title: 'Cancelled Orders',
              value: analytics?.cancelled_orders?.toLocaleString() || '0',
              icon: <Cancel />,
              color: 'error',
            },
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <StatCard color={stat.color}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {stat.title}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: `${stat.color}.light`,
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {React.cloneElement(stat.icon, {
                        sx: { color: `${stat.color}.main`, fontSize: 24 }
                      })}
                    </Box>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
          ))}
        </Grid>

        
      </Box>
    );
  };

  const renderRevenueAnalyticsTab = () => {
    const dailyData = prepareDailyData();
    const categoryData = prepareCategoryData();

    return (
      <Box>
        
      </Box>
    );
  };

  const renderPerformanceTab = () => {
    const popularItemsData = preparePopularItemsData();

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Popular Items */}
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary">
                <Restaurant sx={{ verticalAlign: 'middle', mr: 1 }} />
                Most Popular Items
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <RechartsBarChart data={popularItemsData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    type="number" 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                    width={80}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? `₦${Number(value).toFixed(2)}` : value,
                      name === 'revenue' ? 'Revenue' : 'Sales'
                    ]}
                  />
                  <Legend />
                  <Bar 
                    dataKey="sales" 
                    name="Quantity Sold"
                    fill={CHART_COLORS.primary}
                    radius={[0, 4, 4, 0]}
                  />
                </RechartsBarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Grid>

          {/* Staff Performance */}
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary">
                <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                Performance Insights
              </Typography>
              <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                {analytics?.current_staff_sales > 0 ? (
                  <>
                    <Typography variant="h4" color="primary" gutterBottom>
                      ₦{analytics.current_staff_sales.toFixed(2)}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" gutterBottom>
                      Your Sales Today
                    </Typography>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                      ₦{analytics.current_staff_remittable.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Amount to Remit
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    No performance data available for current staff.
                  </Typography>
                )}
              </Box>
            </ChartContainer>
          </Grid>
        </Grid>

        {/* Additional Insights */}
        {(analytics?.low_stock_items > 0) && (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="subtitle1" fontWeight="bold">
                  ⚠️ Low Stock Alert
                </Typography>
                <Typography variant="body2">
                  You have {analytics.low_stock_items} item(s) running low on stock that need attention.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={handleRefresh} sx={{ ml: 2 }} size="small">
          Retry
        </Button>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="info">
        No analytics data available. Start creating orders to see insights.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={0} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            📊 F&B Analytics Dashboard
          </Typography>
          <Typography variant="subtitle1">
            Comprehensive insights into your food and beverage operations
          </Typography>
        </Paper>

        {/* Controls Section */}
        <Paper elevation={1} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search analytics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Period</InputLabel>
                  <Select value={period} onChange={handlePeriodChange} label="Period">
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs Section */}
        <Paper elevation={1} sx={{ mt: 2, borderRadius: 2 }}>
          <StyledTabs value={activeTab} onChange={handleTabChange} variant="scrollable">
            {tabLabels.map((tab, index) => (
              <StyledTab 
                key={index} 
                label={tab.label}
                icon={tab.icon} 
                iconPosition="start" 
              />
            ))}
          </StyledTabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default FBAnalyticsDashboard;